import { useCallback, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, CheckCircle, Loader2, Lock } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  IndicateurSauvegarde,
  Navbar,
  PageContainer,
  useSauvegardeDifferee,
} from "@repo/ui";
import { useEvaluatorAuthStore, useEvaluatorLogout } from "@repo/auth";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ReferentielConsigne } from "../referentiel/referentiel-consigne";
import {
  ReferentielFourchettes,
  type ReponseFourchette,
} from "../referentiel/referentiel-fourchettes";
import {
  ReferentielQsort,
  type ReponseQsort,
} from "../referentiel/referentiel-qsort";

/** Contenu d'une contribution, quelle que soit l'échelle (§3.6). La validation
 *  de forme est faite côté mutation : ici on transporte. */
type ReponseReferent = ReponseFourchette | ReponseQsort;

/** Délai d'autosave. Assez court pour ne rien perdre, assez long pour ne pas
 *  écrire à chaque cran de curseur. */
const DELAI_BROUILLON_MS = 800;

export function ReferentielView() {
  const { id, notationId } = useParams<{ id: string; notationId: string }>();
  const navigate = useNavigate();
  const token = useEvaluatorAuthStore((s) => s.token);
  const firstName = useEvaluatorAuthStore((s) => s.firstName);
  const { logout } = useEvaluatorLogout();

  const passation = useQuery(
    api.referentiel.passation.obtenirPassationReferent,
    notationId && token
      ? { notationId: notationId as Id<"notation">, identifiant: token }
      : "skip",
  );
  const sauvegarderBrouillon = useMutation(
    api.notation.humaine.sauvegarderBrouillonNotation,
  );
  const soumettre = useMutation(api.notation.humaine.soumettreNotationHumaine);

  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  // Un brouillon encore en attente part quand on quitte la page ou l'écran
  // (le hook le vide) : sinon le dernier geste avant « Retour » était perdu.
  const sauverBrouillon = useCallback(
    (reponses: ReponseReferent[]) => {
      if (!notationId || !token) return Promise.resolve();
      return sauvegarderBrouillon({
        notationId: notationId as Id<"notation">,
        identifiant: token,
        contenu: { reponses },
      });
    },
    [sauvegarderBrouillon, notationId, token],
  );
  const autosave = useSauvegardeDifferee(sauverBrouillon, DELAI_BROUILLON_MS);

  if (passation === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if ("erreur" in passation) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Erreur</h1>
          <p className="mt-2 text-muted-foreground">{passation.erreur}</p>
        </div>
      </div>
    );
  }

  const { config, campagneNom, statut, brouillon, fige } = passation;
  const dejaValidee = statut === "termine";
  const lectureSeule = dejaValidee || fige;

  const planifierBrouillon = (reponses: ReponseReferent[]) => {
    if (lectureSeule || !notationId || !token) return;
    autosave.planifier(reponses);
  };

  const valider = async (reponses: ReponseReferent[]) => {
    if (!notationId || !token) return;
    autosave.annuler();
    setErreur(null);
    setEnvoiEnCours(true);
    try {
      await soumettre({
        notationId: notationId as Id<"notation">,
        identifiant: token,
        contenu: { reponses },
      });
      void navigate(`/pe/${id}`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : String(e));
    } finally {
      setEnvoiEnCours(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar
        title={
          config.cible === "pami"
            ? "Attentes du poste (motivations)"
            : "Priorités culturelles de l'organisation"
        }
        userInfo={{
          displayName: firstName || token || "",
          subtitle: "",
          initials: (firstName || token || "?").charAt(0).toUpperCase(),
        }}
        onLogout={logout}
      />

      <main>
        <PageContainer>
          <Button
            variant="ghost"
            className="mb-4 -ml-2"
            onClick={() => navigate(`/pe/${id}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à mon espace
          </Button>

          <div className="space-y-4">
            <ReferentielConsigne cible={config.cible} campagneNom={campagneNom} />

            {dejaValidee && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="flex items-center gap-3 pt-6 text-green-800">
                  <CheckCircle className="h-5 w-5 shrink-0" />
                  <p>
                    Votre contribution a été validée. Elle n'est plus modifiable —
                    contactez l'administrateur de la campagne si vous devez la
                    corriger.
                  </p>
                </CardContent>
              </Card>
            )}

            {fige && !dejaValidee && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="flex items-center gap-3 pt-6 text-yellow-900">
                  <Lock className="h-5 w-5 shrink-0" />
                  <p>
                    Le référentiel de cette campagne est clôturé : il n'accepte
                    plus de nouvelle contribution.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Q-sort : l'erreur d'envoi s'affiche dans la confirmation, à côté
                du bouton qui l'a provoquée. */}
            {erreur && config.echelle.kind === "fourchette" && (
              <Card className="border-destructive/30 bg-destructive/5">
                <CardContent className="pt-6 text-destructive">{erreur}</CardContent>
              </Card>
            )}

            {config.echelle.kind === "fourchette" ? (
              <ReferentielFourchettes
                config={config}
                brouillon={brouillon}
                lectureSeule={lectureSeule}
                enCoursEnvoi={envoiEnCours}
                onBrouillon={planifierBrouillon}
                onValider={(reponses) => void valider(reponses)}
              />
            ) : (
              <ReferentielQsort
                config={config}
                brouillon={brouillon}
                lectureSeule={lectureSeule}
                enCoursEnvoi={envoiEnCours}
                erreurEnvoi={erreur}
                onEffacerErreur={() => setErreur(null)}
                graineOrdre={notationId}
                indicateurSauvegarde={
                  <IndicateurSauvegarde
                    etat={autosave.etat}
                    onReessayer={autosave.reessayer}
                  />
                }
                onBrouillon={planifierBrouillon}
                onValider={(reponses) => void valider(reponses)}
              />
            )}
          </div>
        </PageContainer>
      </main>
    </div>
  );
}
